import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getLenis } from '../core/scroll';

// Pill nav grows into the menu card. Off-scroll, one small fixed element: height tween is fine here.
export function initNavMenu(): void {
  const nav    = document.getElementById('nav');
  const burger = document.getElementById('burger');
  const panel  = document.getElementById('navPanel');
  if (!nav || !burger || !panel) return;

  let open = false;

  const setOpen = (next: boolean) => {
    if (next === open) return;
    open = next;
    burger.classList.toggle('open', open);
    nav.classList.toggle('is-open', open);
    burger.setAttribute('aria-expanded', String(open));
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');

    gsap.killTweensOf([panel, nav]);
    if (open) {
      gsap.set(panel, { visibility: 'visible' });
      gsap.to(panel, { height: 'auto', duration: 0.6, ease: 'power3.inOut' });
      gsap.to(nav, { borderRadius: 26, duration: 0.6, ease: 'power3.inOut' });
      gsap.fromTo(panel.querySelectorAll('.nav-panel-inner > *'),
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.035, delay: 0.15 });
      panel.querySelector<HTMLElement>('a')?.focus({ preventScroll: true });
    } else {
      gsap.to(panel, {
        height: 0, duration: 0.5, ease: 'power3.inOut',
        onComplete: () => { gsap.set(panel, { visibility: 'hidden' }); },
      });
      gsap.to(nav, { borderRadius: 31, duration: 0.5, ease: 'power3.inOut' });
    }

    const lenis = getLenis();
    if (lenis) {
      if (open) lenis.stop();
      else lenis.start();
    }
  };

  burger.addEventListener('click', () => setOpen(!open));
  panel.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) {
      setOpen(false);
      burger.focus();
    }
  });
  document.addEventListener('pointerdown', (e) => {
    if (open && !nav.contains(e.target as Node)) setOpen(false);
  });
}

export function initScrollReveal(): void {
  document.querySelectorAll<HTMLElement>('.reveal').forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start:   'top 88%',
      onEnter: () => el.classList.add('in'),
      once:    true,
    });
  });
}

export function initAnchorScroll(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href');
      if (!id || id === '#') return;

      const target = document.querySelector(id);
      const lenis = getLenis();
      if (target && lenis) {
        e.preventDefault();
        lenis.scrollTo(target as HTMLElement, {
          offset: 0,
          duration: 1.4,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
      }
    });
  });
}

export function initContactForm(): void {
  // Every form[data-subject] posts to FormSubmit (formsubmit.co): free, no account. The first submission to a new
  // address sends an activation email; after activating, swap the address for FormSubmit's alias to hide it.
  document.querySelectorAll<HTMLFormElement>('form[data-subject]').forEach((form) => {
    const btn = form.querySelector<HTMLButtonElement>('[type="submit"]');
    const status = form.querySelector<HTMLElement>('.form-status');
    if (!btn) return;
    const label = btn.textContent;
    const say = (msg: string, ok: boolean) => {
      if (!status) return;
      status.textContent = msg;
      status.dataset.state = ok ? 'ok' : 'error';
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!form.reportValidity()) return;

      const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
      if (data._honey) return; // bot
      btn.textContent = 'Sending…';
      btn.setAttribute('disabled', '');
      say('', true);

      const who = data.name || `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim();
      try {
        const res = await fetch('https://formsubmit.co/ajax/nrg.praxis@gmail.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...data, _subject: `${form.dataset.subject}: ${who}`, _replyto: data.email, _template: 'table' }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || String(json.success) === 'false') throw new Error(json.message || res.statusText);
        form.reset();
        say('Thank you. It has been sent; we will reply to the email you shared.', true);
      } catch (err) {
        console.warn('FormSubmit:', err); // e.g. "This form needs Activation" on a new address or domain
        say("We couldn't send this just now. Please try again, or write to nrg.praxis@gmail.com.", false);
      } finally {
        btn.textContent = label;
        btn.removeAttribute('disabled');
      }
    });
  });

  // /contact/?service=Name pre-fills the brief and select dropdown from a service page's CTA
  const svc = new URLSearchParams(location.search).get('service');
  const brief = document.querySelector<HTMLTextAreaElement>('#cfBrief');
  const svcSelect = document.querySelector<HTMLSelectElement>('#cfService');
  if (svc) {
    if (svcSelect) {
      const target = svc.toLowerCase().replace(/&/g, 'and');
      for (const opt of svcSelect.options) {
        const val = opt.value.toLowerCase().replace(/&/g, 'and');
        if (val && (val.includes(target) || target.includes(val))) {
          opt.selected = true;
          break;
        }
      }
    }
    if (brief && !brief.value) brief.value = `I'm interested in ${svc}. `;
  }

  // The film option (data-film, set by pages.mjs) asks a few film questions; hidden and disabled otherwise so they aren't sent
  document.querySelectorAll<HTMLFormElement>('.contact-form').forEach((form) => {
    const select = form.querySelector<HTMLSelectElement>('[name="service"]');
    const film = form.querySelector<HTMLFieldSetElement>('.form-film');
    if (!select || !film) return;
    const sync = () => {
      const on = !!select.selectedOptions[0]?.hasAttribute('data-film');
      film.hidden = !on;
      film.disabled = !on;
    };
    select.addEventListener('change', sync);
    form.addEventListener('reset', () => setTimeout(sync));
    sync();
  });
}

export function initMagnetText(): void {
  if (!window.matchMedia('(hover: hover)').matches) return;

  const targets = document.querySelectorAll<HTMLElement>('.nav-link, .nav-logo, .cta-text-link');

  targets.forEach((el) => {
    if (!el.querySelector('.mag-inner')) {
      const wrap = document.createElement('span');
      wrap.className = 'mag-inner';
      wrap.style.display = 'inline-block';
      wrap.style.willChange = 'transform';
      while (el.firstChild) {
        wrap.appendChild(el.firstChild);
      }
      el.appendChild(wrap);
    }

    const inner = el.querySelector<HTMLElement>('.mag-inner');
    if (!inner) return;

    el.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.35;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.3;
      gsap.to(inner, { x, y, duration: 0.9, ease: 'power3.out', overwrite: 'auto' });
    });

    el.addEventListener('mouseleave', () => {
      gsap.to(inner, { x: 0, y: 0, duration: 1.2, ease: 'power3.inOut', overwrite: 'auto' });
    });
  });

  // Buttons that act (menu / close, theme, CTAs, socials, submit, enquire): the whole button glides toward the pointer
  document.querySelectorAll<HTMLElement>('.nav-burger, .theme-toggle, .btn-primary, .btn-outline, .social-btn, .enquire-close').forEach((el) => {
    el.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.3;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.3;
      gsap.to(el, { x, y, scale: 1.05, duration: 0.9, ease: 'power3.out', overwrite: 'auto' });
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { x: 0, y: 0, scale: 1, duration: 1.2, ease: 'power3.inOut', overwrite: 'auto' });
    });
  });
}

export function initButtonLetters(): void {
  if (!window.matchMedia('(hover: hover)').matches) return;

  const buttons = document.querySelectorAll<HTMLElement>('.btn-primary, .btn-outline');

  buttons.forEach((btn) => {
    if (btn.querySelector('.btn-chars-wrap')) return;

    const rawText = btn.textContent || '';
    const hasArrow = rawText.includes('→');
    const cleanText = rawText.replace('→', '').trim();

    btn.innerHTML = '';
    const textContainer = document.createElement('span');
    textContainer.className = 'btn-chars-wrap';
    textContainer.style.display = 'inline-flex';
    textContainer.style.overflow = 'hidden';

    const charSpans: HTMLElement[] = [];
    cleanText.split('').forEach((ch) => {
      const span = document.createElement('span');
      span.className = 'btn-char';
      span.style.display = 'inline-block';
      span.style.willChange = 'transform, opacity';
      span.textContent = ch === ' ' ? '\u00A0' : ch;
      textContainer.appendChild(span);
      charSpans.push(span);
    });
    btn.appendChild(textContainer);

    let arrowSpan: HTMLElement | null = null;
    if (hasArrow) {
      arrowSpan = document.createElement('span');
      arrowSpan.className = 'btn-arrow';
      arrowSpan.textContent = '→';
      arrowSpan.style.display = 'inline-block';
      arrowSpan.style.willChange = 'transform, opacity';
      btn.appendChild(arrowSpan);
    }

    let isHovering = false;
    btn.addEventListener('mouseenter', () => {
      if (isHovering) return;
      isHovering = true;

      const tl = gsap.timeline({
        onComplete: () => { isHovering = false; }
      });
      tl.to(charSpans, {
        scaleY: 0,
        opacity: 0,
        transformOrigin: 'top center',
        duration: 0.16,
        stagger: 0.016,
        ease: 'power2.out',
      });
      tl.to(charSpans, {
        scaleY: 1,
        opacity: 1,
        transformOrigin: 'bottom center',
        duration: 0.22,
        stagger: 0.016,
        ease: 'power2.out',
      }, '>0.06');

      if (arrowSpan) {
        tl.to(arrowSpan, { x: 8, y: -4, opacity: 0, duration: 0.14, ease: 'power2.in' }, '0');
        tl.fromTo(arrowSpan, { x: -8, y: 4, opacity: 0 }, { x: 0, y: 0, opacity: 1, duration: 0.22, ease: 'power2.out' }, '>0.08');
      }
    });
  });
}

/** "Scroll" cue over the WebGL intro: shows at the very top once the intro is running, hides after the first scroll. */
export function initIntroCue(): void {
  const cue = document.getElementById('introCue');
  if (!cue) return;
  const sync = () => cue.classList.toggle('show', window.scrollY < 40 && !!document.querySelector('.webgl--intro'));
  window.addEventListener('scroll', sync, { passive: true });
  setTimeout(sync, 600); // after the preloader hands over
}

// Get in touch: clicking the section (not its own links), or Enter while it has focus, opens the form in a <dialog>
export function initEnquire(): void {
  const zone = document.querySelector<HTMLElement>('[data-enquire]');
  const dialog = document.getElementById('enquireDialog') as HTMLDialogElement | null;
  if (!zone || !dialog) return;
  const open = () => {
    dialog.showModal();
    dialog.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  };
  zone.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (!dialog.contains(t) && !t.closest('a, button')) open();
  });
  zone.addEventListener('keydown', (e) => { if (e.target === zone && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(); } });
  dialog.querySelector('[data-enquire-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); }); // click on the backdrop
}
